import { getCommonBounds, getElementBounds } from '../../lib/bound';
import { colors } from '../../lib/config';
import { sceneCoordsToViewportCoords } from '../../lib/coords';
import { setCursor } from '../../lib/cursor';
import {
  deleteMultipleElements,
  notifyElementsChanged,
  sortElementsByLayer,
  updateElement,
  updateMultipleElements,
  type GenericElement,
} from '../../lib/element';
import {
  exportEditorElements,
  importEditorElements,
} from '../../lib/exportImport';
import { history } from '../../lib/history';
import { type Plugin } from '../../lib/plugin';
import {
  drawElementFromCanvas,
  generateElementCanvas,
  type OverwriteState,
} from '../../lib/render';
import {
  hideNewElementCanvas,
  renderBooleanElementsWithMasking,
  renderInteractiveScene,
  renderStaticScene,
} from '../../lib/scene';
import {
  calculateZoomToFitStateFromBounds,
  getNextZoomSnap,
  getNormalizedZoom,
  getStateForZoom,
} from '../../lib/zoom';
import { updateCursorStore } from '../../stores/useCursorStore';
import {
  updateRuntimeStore,
  useEditorRuntimeStore,
} from '../../stores/useEditorRuntimeStore';
import { updateEditorStore, useEditorStore } from '../../stores/useEditorStore';
import { logImageToConsole } from '../../utils/debug';
import { type BooleanElement } from '../boolean';
import { type ImageElement } from '../image';

export const general = {
  actions: {
    activate: (
      tool: string,
      customCurserActive = false,
      deselectElements = true,
    ) => {
      const { readonly } = useEditorRuntimeStore.getState();
      if (readonly && tool !== 'selection' && tool !== 'hand') return;

      if (tool === 'hand') setCursor('grab');

      updateRuntimeStore({ activeTool: tool, newElement: undefined });
      updateCursorStore({ customCursorActive: customCurserActive });

      if (
        useEditorStore.getState().selectedElementIds.length &&
        deselectElements
      ) {
        updateEditorStore({ selectedElementIds: [] });
      }

      renderInteractiveScene();
      hideNewElementCanvas();
    },
    canRedo: (index: number) => history.canRedo(index),
    canUndo: (index: number) => history.canUndo(index),
    registerExternalHistoryCallbacks: history.registerExternalStateCallbacks,
    unregisterExternalHistoryCallbacks:
      history.unregisterExternalStateCallbacks,
    captureHistorySnapshot: () => {
      const editorState = useEditorStore.getState();
      history.push(editorState);
    },
    pauseHistory: () => {
      history.pause();
    },
    resumeHistory: () => {
      history.resume();
    },
    redo: () => {
      history.redo();
      renderStaticScene();
      renderInteractiveScene();
    },
    zoomToFit: () => {
      const elementState = useEditorStore.getState();
      const elements = Array.from(elementState.elements.values()).filter(
        (el) => el.isVisible,
      );

      if (elements.length === 0) return;

      const [x1, y1, x2, y2, centerX, centerY] = getCommonBounds(elements);

      const newState = calculateZoomToFitStateFromBounds({
        x1,
        y1,
        x2,
        y2,
        centerX,
        centerY,
      });

      if (!newState) return;

      updateRuntimeStore(newState);
      renderStaticScene();
      renderInteractiveScene();
    },
    zoomToFitElement: (element: GenericElement) => {
      const [x1, y1, x2, y2, centerX, centerY] = getElementBounds(element);

      const newState = calculateZoomToFitStateFromBounds({
        x1,
        y1,
        x2,
        y2,
        centerX,
        centerY,
      });

      if (!newState) return;

      updateRuntimeStore(newState);
      renderStaticScene();
      renderInteractiveScene();
    },
    resetZoom: () => {
      updateRuntimeStore({
        offsetLeft: 0,
        offsetTop: 0,
        scrollX: 0,
        scrollY: 0,
        zoom: { value: 1 },
      });
      renderStaticScene();
      renderInteractiveScene();
    },
    undo: () => {
      history.undo();
      renderStaticScene();
      renderInteractiveScene();
    },
    zoomIn: () => {
      const state = useEditorRuntimeStore.getState();
      const nextZoom = getNextZoomSnap(state.zoom.value, 'in');
      updateRuntimeStore(
        getStateForZoom({
          nextZoom: getNormalizedZoom(nextZoom),
          viewportX: window.innerWidth / 2 + state.offsetLeft,
          viewportY: window.innerHeight / 2 + state.offsetTop,
        }),
      );
      renderStaticScene();
      renderInteractiveScene();
    },
    zoomOut: () => {
      const state = useEditorRuntimeStore.getState();
      const nextZoom = getNextZoomSnap(state.zoom.value, 'out');
      updateRuntimeStore(
        getStateForZoom({
          nextZoom: getNormalizedZoom(nextZoom),
          viewportX: window.innerWidth / 2 + state.offsetLeft,
          viewportY: window.innerHeight / 2 + state.offsetTop,
        }),
      );
      renderStaticScene();
      renderInteractiveScene();
    },
    setZoom: (zoom: number) => {
      const state = useEditorRuntimeStore.getState();
      updateRuntimeStore(
        getStateForZoom({
          nextZoom: getNormalizedZoom(zoom),
          viewportX: window.innerWidth / 2 + state.offsetLeft,
          viewportY: window.innerHeight / 2 + state.offsetTop,
        }),
      );
      renderStaticScene();
      renderInteractiveScene();
    },
    updateTheme: (theme: 'light' | 'dark') => {
      const config = useEditorRuntimeStore.getState().config;

      config.canvas.fillStyle = colors.canvasFill[theme];
      config.selection.fillStyle = colors.selectionFill[theme];
      config.selection.strokeStyle = colors.selectionStroke[theme];
      config.selection.transformFillStyle =
        colors.selectionTransformFill[theme];

      updateRuntimeStore({ config });
      renderStaticScene();
      renderInteractiveScene();
    },
    deleteElements: (ids: string[]) => {
      if (useEditorRuntimeStore.getState().readonly) return;
      const state = useEditorStore.getState();

      deleteMultipleElements(state.elements, ids);

      notifyElementsChanged();
      renderStaticScene();
      renderInteractiveScene();
    },
    getElement: <T = GenericElement>(id: string) => {
      const state = useEditorStore.getState();
      return state.elements.get(id) as T;
    },
    updateElements: <T>(ids: string[], updates: Partial<T>[]) => {
      if (useEditorRuntimeStore.getState().readonly) return;
      const elements = useEditorStore.getState().elements;
      const originals = ids.map((id) => elements.get(id)!);
      updateMultipleElements(elements, originals, updates);
      renderStaticScene();
    },
    updateElement: <T>(id: string, update: Partial<T>) => {
      if (useEditorRuntimeStore.getState().readonly) return;
      const elements = useEditorStore.getState().elements;
      const original = elements.get(id)!;
      updateElement(elements, original, update);
      renderStaticScene();
    },
    renderStaticScene: () => renderStaticScene(),
    setCursor: (cursor: string) => setCursor(cursor),
    sceneCoordsToViewportCoords: (pos: { x: number; y: number }) =>
      sceneCoordsToViewportCoords(pos),
    exportEditor: async () => await exportEditorElements(),
    importEditor: async (
      plugins: Plugin[],
      payload: { elements: GenericElement[] },
      options?: { allLocked?: boolean; clearHistory?: boolean },
    ) => {
      if (options?.clearHistory !== false) {
        history.clear();
      }
      await importEditorElements(plugins, payload, options);
      history.push(useEditorStore.getState());
    },
    exportThumbnail: async (maxResolution?: number) => {
      const editorState = useEditorStore.getState();
      const elements = Array.from(editorState.elements.values()).filter(
        (el) =>
          el.isVisible && el.type !== 'grid' && el.type !== 'colorchecker',
      );

      const imageElement = elements.find(
        (element): element is ImageElement => element.type === 'image',
      );

      if (!imageElement) {
        console.warn('no image found');
        return null;
      }

      let targetWidth = imageElement.image.naturalWidth;
      let targetHeight = imageElement.image.naturalHeight;

      if (maxResolution != null && maxResolution > 0) {
        const longerSide = Math.max(targetWidth, targetHeight);
        if (longerSide > maxResolution) {
          const scale = maxResolution / longerSide;
          targetWidth = Math.round(targetWidth * scale);
          targetHeight = Math.round(targetHeight * scale);
        }
      }

      const scaleX = targetWidth / imageElement.width;
      const scaleY = targetHeight / imageElement.height;

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('Failed to create 2D context for thumbnail generation.');
        return null;
      }

      ctx.save();
      ctx.scale(scaleX, scaleY);

      const overwriteState: OverwriteState = {
        offsetX: -imageElement.x,
        offsetY: -imageElement.y,
        zoom: scaleX,
        skipElementUpdate: true,
        isExportImage: true,
      };

      const sortedElements = elements.toSorted(sortElementsByLayer);
      const grouped = Object.groupBy(sortedElements, (el) =>
        el.type === 'boolean' ? 'boolean' : 'nonBoolean',
      );
      const booleanElements = (grouped.boolean ?? []) as BooleanElement[];
      const nonBooleanElements = grouped.nonBoolean ?? [];

      for (const element of nonBooleanElements) {
        const elementCanvas = generateElementCanvas(element, overwriteState);
        if (!elementCanvas) continue;
        drawElementFromCanvas(element, elementCanvas, ctx, overwriteState);
      }

      renderBooleanElementsWithMasking(
        booleanElements,
        ctx,
        false,
        overwriteState,
      );

      ctx.restore();

      if (import.meta.env.MODE === 'development') {
        logImageToConsole(canvas.toDataURL(), targetHeight, targetWidth);
      }

      return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(async (blob) => {
          if (blob) return resolve(blob);
          else reject(new Error('Failed to compress image'));
        });
      });
    },
  },
};
